import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { getCollectibleImageUrl } from '../../../../post-wrap/post-wrap-images';
import { useUserContentDismissal } from '../../../../profile/user-content-dismissal';

interface GiftWrappedBonusProps {
	bonus: Bonus;
	isUserReceiver: boolean;
	children: ReactNode;
}

export const GiftWrappedBonus: FC<GiftWrappedBonusProps> = ({ bonus, isUserReceiver, children }) => {
	const { t } = useTranslation('recognition');
	const [isUnwrapping, setIsUnwrapping] = useState(false);
	const [hasUnwrapped, setHasUnwrapped] = useState(false);

	const style = bonus.visual_state?.gift_wrap_style ?? '';
	const color = bonus.visual_state?.gift_wrap_color ?? '';
	const wrappedImageUrl = getCollectibleImageUrl(style, color, 1);

	const { loading, data, updateUserContentDismissal } = useUserContentDismissal({
		name: `gift-wrapped-${bonus.id}`,
		namespace: 'free-rewards',
		enabled: !!bonus.visual_state?.gift_wrapped && isUserReceiver,
	});

	if (!bonus.visual_state?.gift_wrapped) {
		return <>{children}</>;
	}

	// While loading as receiver, show wrapped state to prevent flash of unwrapped content
	const isDismissed = !loading && !!data?.dismissed_at;

	// Show overlay when: receiver, not dismissed, not yet unwrapped in this session
	const showOverlay = isUserReceiver && !isDismissed && !hasUnwrapped && wrappedImageUrl;

	const handleUnwrap = () => {
		setIsUnwrapping(true);
		setTimeout(() => {
			updateUserContentDismissal();
			setHasUnwrapped(true);
		}, 600);
	};

	// Create unique class names based on bonus ID to prevent animation conflicts
	const uniqueId = bonus.id.replace(/[^a-zA-Z0-9]/g, '');

	if (!showOverlay) {
		return (
			<Box
				sx={{
					'@keyframes revealGiftPresent': {
						'0%': { opacity: 0, transform: 'scale(0.9)' },
						'100%': { opacity: 1, transform: 'scale(1)' },
					},
				}}
				animation={hasUnwrapped ? 'revealGiftPresent 0.4s ease-out' : undefined}
			>
				{children}
			</Box>
		);
	}

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			role="region"
			aria-label={t('gift_wrap.wrapped_aria_label', { defaultValue: 'You received a gift-wrapped recognition! Click unwrap to reveal.' })}
		>
			<style>
				{`
					@keyframes unwrapGift-${uniqueId} {
						0% {
							transform: scale(1) rotate(0deg);
							opacity: 1;
						}
						30% {
							transform: scale(1.1) rotate(-3deg);
						}
						50% {
							transform: scale(1.15) rotate(3deg);
						}
						70% {
							transform: scale(1.2) rotate(-2deg);
						}
						100% {
							transform: scale(1.5) rotate(0deg);
							opacity: 0;
						}
					}

					@keyframes shimmerGift-${uniqueId} {
						0% {
							background-position: -200% center;
						}
						100% {
							background-position: 200% center;
						}
					}

					.gift-wrapped-shimmer-${uniqueId} {
						animation: shimmerGift-${uniqueId} 3s ease-in-out infinite;
					}
				`}
			</style>

			{/* The actual bonus content (hidden from assistive tech while wrapped) */}
			<Box visibility="hidden" aria-hidden="true">
				{children}
			</Box>

			{/* Wrapping paper overlay */}
			<Box
				position="absolute"
				top={0}
				left={0}
				right={0}
				bottom={0}
				backgroundImage={`url(${wrappedImageUrl})`}
				backgroundSize="cover"
				backgroundPosition="center"
				borderRadius="lg"
				zIndex={1}
				role="img"
				aria-label={t('gift_wrap.wrapping_paper_aria', { defaultValue: 'Gift wrapping paper' })}
				style={{
					animation: isUnwrapping ? `unwrapGift-${uniqueId} 0.6s ease-out forwards` : 'none',
				}}
			>
				{/* Subtle shimmer effect - purely decorative */}
				<Box
					className={`gift-wrapped-shimmer-${uniqueId}`}
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					background="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)"
					backgroundSize="200% 100%"
					borderRadius="lg"
					pointerEvents="none"
					aria-hidden="true"
				/>

				{/* Unwrap button */}
				<Flex position="absolute" bottom={4} right={4} alignItems="center" gap={2}>
					<Button
						size="md"
						bg="white"
						color="gray.800"
						fontWeight="medium"
						borderRadius="md"
						boxShadow="lg"
						px={6}
						py={4}
						transform="rotate(-4deg)"
						onClick={handleUnwrap}
						isLoading={isUnwrapping}
						aria-label={t('gift_wrap.unwrap_button_aria', { defaultValue: 'Unwrap this gift to reveal your recognition' })}
						aria-busy={isUnwrapping}
						_hover={{
							bg: 'gray.100',
							transform: 'rotate(-4deg) scale(1.05)',
						}}
						_active={{
							bg: 'gray.200',
							transform: 'rotate(-4deg) scale(0.98)',
						}}
						transition="all 0.2s"
					>
						{t('gift_wrap.unwrap_button', { defaultValue: 'Unwrap me!' })}
					</Button>
				</Flex>
			</Box>
		</Box>
	);
};
